import { useDisclosure } from '@chakra-ui/hooks';
import GoBack from 'components/actions/GoBack';
import CreateButton from 'components/buttons/CreateButton';
import Card from 'components/card';
import InputField from 'components/fields/InputField';
import SearchIcon from 'components/icons/SearchIcon';
import { getCustomer } from 'interfaces/customer';
import {
    createPricings,
    deletePricing,
    getPricing,
    updatePricings,
} from 'interfaces/pricing';
import { getProductsPurchasePrice } from 'interfaces/products';
import AddProductsModal from 'logicComponent/addProductsModal';

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

const EditCustomerPricing = () => {
    const navigate = useNavigate();
    const params = useParams();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [customer, setCustomer] = useState<any>({});
    const [pricings, setPricings] = useState<any>([]);
    const [marginProducts, setMarginProducts] = useState<any>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [meta, setMeta] = useState<any>(null);
    const [search, setSearch] = useState('');

    const initCustomer = async (id: string) => {
        const res = await getCustomer(id);
        if (res) {
            setCustomer(res);
            return;
        }
        navigate('/customers');
    };

    const editPricing = async () => {
        const pricingToUpdate = pricings.map((item: any) => ({
            _id: item._id,
            price: item.price,
            customerId: item.customerId,
        }));
        const res = await updatePricings({ pricings: pricingToUpdate });
        if (res) toast.success('Les prix ont été mis à jour avec succès');
    };

    const removePricing = async (id: string) => {
        const res = await deletePricing(id);
        if (res)
            setPricings((prevPricings: any) =>
                prevPricings.filter((item: any) => item._id !== id),
            );
    };

    const handlePriceChange = (e: any, item: any) => {
        try {
            const value = e.target.value;
            const editedPricings = [...pricings].map((p: any) => {
                if (p._id === item._id) return { ...p, price: value };
                return p;
            });
            setPricings(editedPricings);
        } catch (error) {
            console.error('Erreur modification prix:', error);
        }
    };

    const TableHead = () => {
        return (
            <thead>
                <tr key={0} className="!border-px !border-gray-400">
                    <th
                        key={1}
                        colSpan={0}
                        onClick={() => {}}
                        className="mt-[20px] cursor-pointer border-b border-gray-200 pb-2 pl-7 pt-4 text-start text-sm dark:border-white/10"
                    >
                        <p className="text-sm font-bold text-gray-600 dark:text-white">
                            Produit
                        </p>
                    </th>
                    <th
                        key={2}
                        colSpan={0}
                        onClick={() => {}}
                        className="mt-[20px] cursor-pointer border-b border-gray-200 pb-2 pl-7 pt-4 text-start text-sm dark:border-white/10"
                    >
                        <p className="text-sm font-bold text-gray-600 dark:text-white">
                            Prix unitaire HT (€)
                        </p>
                    </th>
                    <th
                        key={3}
                        colSpan={0}
                        onClick={() => {}}
                        className="mt-[20px] cursor-pointer border-b border-gray-200 pb-2 pl-7 pt-4 text-start text-sm dark:border-white/10"
                    >
                        <p className="text-sm font-bold text-gray-600 dark:text-white">
                            Unité
                        </p>
                    </th>
                    <th
                        key={4}
                        colSpan={0}
                        onClick={() => {}}
                        className="mt-[20px] cursor-pointer border-b border-gray-200 pb-2 pl-7 pt-4 text-start text-sm dark:border-white/10"
                    >
                        <p className="text-sm font-bold text-gray-600 dark:text-white">
                            Actions
                        </p>
                    </th>
                </tr>
            </thead>
        );
    };

    const handleNewPricing = async (products: any) => {
        
        // Filter out products that already have a pricing with the same packaging
        const productsToAdd = products.filter((item: any) => {
            const exists = pricings.some((p: any) => {
                const sameProduct = p.productId === item._id;
                const samePackaging = (p.packagingRef || null) === (item.packagingRef || null);
                return sameProduct && samePackaging;
            });
            return !exists;
        });

        if (productsToAdd.length === 0) {
            onClose();
            return;
        }

        const productsToSent = productsToAdd.map((item: any) => {
            
            const productData: any = {
                _id: item._id,
                price: item.price || 0,
                name: item.name,
                unity: item.unity,
                ref: String(item.ref || ''),
            };
            
            // Add packaging information if present (check for packagingUnity or selectedPackaging)
            if (item.packagingUnity || item.selectedPackaging) {
                productData.packagingRef = item.packagingRef || (item.selectedPackaging?.ref || '');
                productData.packagingUnity = item.packagingUnity || item.selectedPackaging?.unity;
                productData.packagingWeight = item.packagingWeight || item.selectedPackaging?.weight;
            }
            
            return productData;
        });
        
        
        const newPricing = await createPricings({
            customerId: customer._id,
            products: productsToSent,
        });
        if (newPricing)
            setPricings((prevPricings: any) => [
                ...prevPricings,
                ...newPricing,
            ]);
        onClose();
    };

    const initPricing = async (id: string) => {
        setIsLoading(true);
        const res = await getPricing(id, 1, 100);
        if (res) {
            setPricings(res.data || []);
            setMeta(res.meta);
            setCurrentPage(1);
            setHasMore(res.meta?.hasNextPage || false);
            
            // Charger les marges une seule fois après le chargement initial
            if (res.data && res.data.length > 0) {
                loadPricingsMargins(res.data);
            }
        }
        setIsLoading(false);
    };

    const loadMorePricings = async () => {
        if (isLoading || !hasMore || !params.id) return;
        
        setIsLoading(true);
        const nextPage = currentPage + 1;
        const res = await getPricing(params.id, nextPage, 100);
        
        if (res && res.data) {
            const newPricings = [...pricings, ...res.data];
            setPricings(newPricings);
            setCurrentPage(nextPage);
            setHasMore(res.meta?.hasNextPage || false);
            
            // Charger les marges uniquement pour les nouveaux produits
            if (res.data.length > 0) {
                loadPricingsMargins(res.data);
            }
        }
        setIsLoading(false);
    };

    const loadPricingsMargins = async (pricingsToLoad: any[]) => {
        try {
            const productIds = pricingsToLoad.map((item: any) => item.productId).filter(Boolean);
            if (productIds.length === 0) return;
            
            const res = await getProductsPurchasePrice(productIds);
            if (!res || !res.data) return;
            
            const newMargins = pricingsToLoad.map((item: any) => {
                const product = res.data.find(
                    (product: any) => product.id === item.productId,
                );
                return {
                    ...item,
                    totalPurchasePrice: product?.marginData?.purchasePrice || 0,
                };
            });
            
            setMarginProducts((prev: any[]) => {
                // S'assurer que prev est bien un tableau
                const prevArray = Array.isArray(prev) ? prev : [];
                const existing = prevArray.filter(
                    (m: any) => !newMargins.some((n: any) => n.productId === m.productId)
                );
                return [...existing, ...newMargins];
            });
        } catch (error) {
            console.error('Erreur lors du chargement des marges:', error);
        }
    };

    const initProductsPrice = async () => {
        try {
            const productIds = pricings.map((item: any) => item.productId).filter(Boolean);
            if (productIds.length === 0) {
                setMarginProducts([]);
                return;
            }
            const res = await getProductsPurchasePrice(productIds);
            if (!res || !res.data) {
                setMarginProducts([]);
                return;
            }
            const newItems = pricings.map((item: any) => {
                const product = res.data.find(
                    (product: any) => product.id === item.productId,
                );
                return {
                    ...item,
                    totalPurchasePrice: product?.marginData?.purchasePrice || 0,
                };
            });
            setMarginProducts(newItems);
        } catch (error) {
            console.error('Erreur lors du chargement des prix d\'achat:', error);
            setMarginProducts([]);
        }
    };

    const getMarginString = (item: any, sellPrice: number) => {
        try {
            if (!item || !sellPrice || isNaN(sellPrice) || sellPrice <= 0) return null;
            const purchasePrice = item.totalPurchasePrice;
            const totalCost = purchasePrice;

            if (!totalCost || isNaN(totalCost) || totalCost <= 0) return null;
            const margin = sellPrice - totalCost;
            const marginPercent = ((margin / sellPrice) * 100);
            
            if (isNaN(margin) || isNaN(marginPercent)) return null;
            
            return `Marge : ${margin.toFixed(2).replace(/\.?0+$/, '')} € (${marginPercent.toFixed(2).replace(/\.?0+$/, '')} %)`;
        } catch (error) {
            console.error('Erreur calcul marge:', error);
            return null;
        }
    };

    useEffect(() => {
        if (localStorage.getItem('token') === null) {
            navigate('/login');
        }
        initCustomer(params.id);
        initPricing(params.id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            if (
                window.innerHeight + document.documentElement.scrollTop
                >= document.documentElement.offsetHeight - 500
            ) {
                loadMorePricings();
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, isLoading, hasMore]);

    return (
        <div className="">
            <AddProductsModal
                isOpen={isOpen}
                onClose={onClose}
                addProducts={(p: any) => handleNewPricing(p)}
                existingProducts={[]}
                customerId={params.id}
            />
            <div className={``}>
                <GoBack text="Fiche client" />
                <Card extra={'mt-2 p-4 border'}>
                    {/* Header */}
                    <div className="w-full px-5">
                        <h4 className="text-lg font-bold text-navy-700 dark:text-white">
                            {customer
                                ? `Prix personnalisées - ${customer.company}`
                                : 'Prix personnalisées'}
                        </h4>
                        <p className="mt-3 text-base text-gray-600">
                            Vous pouvez proposer des prix personnalisés à{' '}
                            {customer ? customer.company : 'Ce client'}.
                        </p>
                    </div>
                    <div className="mt-2 grid grid-cols-12">
                        <div className="col-span-12">
                            {pricings.length > 0 ? (
                                <div className="">
                                    <div className="flex ml-2 mt-5 gap-2">
                                        <div className="">
                                            <CreateButton
                                                extra="flex text-xs py-2 px-1"
                                                value="+ produits"
                                                onClick={onOpen}
                                            />
                                        </div>
                                        <div className="mt-1 w-[350px]">
                                            <div className="flex h-[32px] items-center rounded-xl bg-lightPrimary text-sm text-gray-600">
                                                <SearchIcon />
                                                <input
                                                    type="text"
                                                    placeholder="Rechercher un produit..."
                                                    value={search}
                                                    onChange={(e) => setSearch(e.target.value)}
                                                    className="text-sm block w-full rounded-full bg-lightPrimary text-navy-700 outline-none"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <table className="mt-5 w-full">
                                        <TableHead />
                                        <tbody>
                                            {pricings
                                                ?.filter((p: any) => {
                                                    if (!search) return true;
                                                    const s = search.toLowerCase();
                                                    return (
                                                        (p.name || '').toLowerCase().includes(s) ||
                                                        String(p.ref || '').toLowerCase().includes(s)
                                                    );
                                                })
                                                .map(
                                                (item: any, index: number) => (
                                                    <tr key={`${index}`}>
                                                        <td
                                                            key={0}
                                                            className="mt-[20px] border-b px-[100px] py-[32px] pl-7 text-sm dark:border-white/10"
                                                        >
                                                            <div className="col-span-12">
                                                                <p className="w-full text-sm font-bold text-navy-700 dark:text-white">
                                                                    {item.name ||
                                                                        'Produit'}
                                                                    {item.packagingUnity && (
                                                                        <span className="ml-2 text-xs font-medium text-toola-500">
                                                                            [{item.packagingUnity}]
                                                                        </span>
                                                                    )}
                                                                </p>
                                                                <p className="font-base mt-[2px] text-gray-600">
                                                                    #{item.ref}{' '}
                                                                    {item.packagingUnity && (
                                                                        <span className="ml-2 text-xs text-gray-500">
                                                                            • {item.packagingWeight} {item.unity} par {item.packagingUnity}
                                                                        </span>
                                                                    )}
                                                                </p>
                                                            </div>
                                                        </td>
                                                        <td
                                                            key={1}
                                                            className="min-w-[60px] mt-[20px] border-b px-auto py-[32px] pl-7 text-sm dark:border-white/10"
                                                        >
                                                            <InputField
                                                                id="price"
                                                                value={
                                                                    item.price
                                                                }
                                                                type="number"
                                                                inputSmall={
                                                                    true
                                                                }
                                                                onChange={(
                                                                    e: any,
                                                                ) =>
                                                                    handlePriceChange(
                                                                        e,
                                                                        item,
                                                                    )
                                                                }
                                                            />
                                                            <p className="mt-1">
                                                                {getMarginString(
                                                                    marginProducts?.find(
                                                                        (
                                                                            p: any,
                                                                        ) =>
                                                                            p.productId ===
                                                                            item.productId,
                                                                    ),
                                                                    item.price,
                                                                )}
                                                            </p>
                                                        </td>
                                                        <td
                                                            key={2}
                                                            className="mt-[20px] border-b px-[100px] py-[32px] pl-7 text-sm dark:border-white/10"
                                                        >
                                                            <p className="text-sm font-bold text-navy-700 dark:text-white">
                                                                {item.packagingUnity || item.unity || 'N/a'}
                                                            </p>
                                                        </td>
                                                        <td
                                                            key={3}
                                                            className="mt-[20px] border-b px-[100px] py-[32px] pl-7 text-sm dark:border-white/10"
                                                        >
                                                            <button
                                                                className="linear col-span-3 flex items-center justify-center rounded-xl bg-red-400 px-3 py-3 text-sm font-medium text-white hover:bg-red-500 active:bg-red-600"
                                                                onClick={() =>
                                                                    removePricing(
                                                                        item._id,
                                                                    )
                                                                }
                                                            >
                                                                Retirer
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ),
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="rounded-xl border bg-white/0 outline-none">
                                    <p className="ml-3 mt-6 text-center">
                                        Vous n'avez pas encore défini de prix
                                        personnalisés pour ce client.
                                    </p>
                                    <CreateButton
                                        extra="mt-3 mb-5 align-center mx-auto flex text-xs py-2 px-1"
                                        value="Ajouter un prix personnalisé"
                                        onClick={onOpen}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="mt-10 grid grid-cols-12 gap-5">
                        <div className="col-span-3">
                            <button
                                className="flex items-center justify-center rounded-xl bg-[#0260CB] px-5 py-3 text-sm font-medium text-white hover:bg-[#01499b] active:bg-[#024086]"
                                onClick={() => editPricing()}
                            >
                                Sauvegarder
                            </button>
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default EditCustomerPricing;
